import { useEffect, useRef } from "react";
import type { RefObject } from "react";

// 鍵盤相關的共用工具:全域快捷鍵、焦點陷阱、清單上下鍵選擇。
// 拖曳與滑鼠不是唯一操作路徑,所有互動都必須有鍵盤替代方案。

/** 可聚焦元素的選擇器,焦點陷阱與自動聚焦都以此為準。 */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/** 目前是否在 macOS。決定快捷鍵顯示成 ⌘K 還是 Ctrl+K。 */
export function isMacPlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  const platform = navigator.platform ?? "";
  const agent = navigator.userAgent ?? "";
  return /Mac|iPhone|iPad|iPod/i.test(`${platform} ${agent}`);
}

/** 快捷鍵的顯示字串,給按鈕上的提示用。 */
export function commandPaletteHint(): string {
  return isMacPlatform() ? "⌘K" : "Ctrl+K";
}

/** 是否為開啟全域搜尋的組合鍵:⌘K(mac)或 Ctrl+K(其他)。 */
export function isCommandPaletteShortcut(event: KeyboardEvent): boolean {
  if (event.key !== "k" && event.key !== "K") return false;
  // 兩種修飾鍵都接受,跨平台使用者不必記自己在哪台機器上。
  return event.metaKey || event.ctrlKey;
}

/** 焦點是否落在輸入類元素上。快捷鍵要避開使用者正在打字的欄位。 */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select";
}

/**
 * 在 window 上掛 keydown。掛在 window 而非 document,
 * 是因為測試與程式都可能直接對 window 發事件,window 收得到全部。
 */
export function useGlobalKeydown(handler: (event: KeyboardEvent) => void, enabled = true): void {
  // 用 ref 保存最新的 handler,避免每次 render 都重新掛載監聽器。
  const saved = useRef(handler);
  saved.current = handler;

  useEffect(() => {
    if (!enabled) return;
    const listener = (event: KeyboardEvent) => saved.current(event);
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [enabled]);
}

/** 取得容器內所有可聚焦元素,依 DOM 順序。 */
export function getFocusableElements(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement || el.getClientRects().length > 0,
  );
}

/**
 * 焦點陷阱:對話框開啟時,Tab 只在容器內循環,不會跑到後面的頁面。
 * 這是 aria-modal 的必要配套,只寫 aria-modal 而不困住焦點等於沒做。
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const root = ref.current;
      if (!root) return;
      const focusable = getFocusableElements(root);
      if (focusable.length === 0) {
        event.preventDefault();
        root.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;
      // 兩支刻意對稱:**焦點不在容器裡時一律拉回來**,不是只有走到頭才管。
      //
      // 對話框裡刪掉一列(撤銷一筆開票申請、刪掉一期付款條件)之後,使用者剛
      // 按下的那顆鈕跟著那一列被卸載,`current` 就是 `<body>`。往前這一支只看
      // `current === last` 的話,兩個條件都不成立、不 `preventDefault()`,
      // 瀏覽器會把焦點送到整份文件的第一個可聚焦元素 —— 也就是遮罩**後面**的
      // 頁面:焦點環看不到(遮罩蓋著、背景 overflow:hidden 捲不過去),而
      // `aria-modal` 正告訴螢幕閱讀器那些東西不存在。
      if (event.shiftKey && (current === first || !root.contains(current))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (current === last || !root.contains(current))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [ref, active]);
}

/** 對話框關閉後把焦點還給原本的觸發元素,鍵盤使用者才不會被丟回頁首。 */
export function useRestoreFocus(active: boolean): void {
  const previous = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (active) {
      previous.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      return;
    }
    const target = previous.current;
    previous.current = null;
    if (target && document.contains(target)) target.focus();
  }, [active]);

  useEffect(() => {
    return () => {
      const target = previous.current;
      if (target && document.contains(target)) target.focus();
    };
  }, []);
}

/** 清單上下鍵選擇用的索引環繞;長度為 0 時固定回 -1。 */
export function wrapIndex(current: number, length: number, delta: number): number {
  if (length <= 0) return -1;
  const next = (current + delta) % length;
  return next < 0 ? next + length : next;
}
